import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core'
import {
    arrayMove,
    sortableKeyboardCoordinates,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import { Button, TextField } from '@bosch/react-frok'
import useWindowSize from '@/hooks/useWindowSize'
import FrokMultiSelect from '../FrokMultiSelect'
import LinePicker from './LinePicker'
import SwipeToDeleteRow from './SwipeToDeleteRow'

import './DraggableLineSelection.scss'

type RowItem = { id: string; value: string }
type LineOption = { value: string; label: string }

type DragHandleProps = {
    ref: (element: HTMLElement | null) => void
} & React.HTMLAttributes<HTMLElement>

interface SortableRowProps {
    id: string
    children: (dragHandleProps: DragHandleProps) => React.ReactNode
}

// Drag activation is scoped to a handle (via dragHandleProps) rather than the
// whole row, since the row also hosts the select menu's inline-rendered options.
const preventTouchScroll = (e: TouchEvent) => e.preventDefault()

const SortableRow = ({ id, children }: SortableRowProps) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
        useSortable({ id })
    const handleElement = useRef<HTMLElement | null>(null)

    // iOS Safari scrolls the page under a touch that starts on the handle in spite of `touch-action: none`: the handle
    // takes its touchmoves (React registers its own touchmove listeners as passive, so this one is native)
    const handleRef = useCallback(
        (element: HTMLElement | null) => {
            handleElement.current?.removeEventListener('touchmove', preventTouchScroll)
            handleElement.current = element
            element?.addEventListener('touchmove', preventTouchScroll, { passive: false })
            setActivatorNodeRef(element)
        },
        [setActivatorNodeRef]
    )

    return (
        <div
            ref={setNodeRef}
            className={`draggable-line-selection__line-row${isDragging ? ' draggable-line-selection__line-row--dragging' : ''}`}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                zIndex: isDragging ? 1 : undefined,
            }}
        >
            {children({ ref: handleRef, ...attributes, ...listeners })}
        </div>
    )
}

const MAX_LINES_CAN_BE_SELECTED = 10

export interface DraggableLineSelectionProps {
    availableLines: LineOption[]
    rows: RowItem[]
    setRows: React.Dispatch<React.SetStateAction<RowItem[]>>
    /** Prefix for each row label. Rendered as "{labelPrefix} {index + 1}". */
    labelPrefix?: string
    /** Placeholder text for the line dropdown. Falls back to FrokMultiSelect default. */
    placeholder?: string
    /**
     * Called after every selection, removal, or reorder with the resolved
     * filled lines in their current order. Use for immediate persistence
     * (e.g. Settings auto-save) instead of a submit button.
     */
    onChange?: (selectedLines: LineOption[]) => void
}

const DraggableLineSelection = ({
    availableLines,
    rows,
    setRows,
    labelPrefix,
    placeholder,
    onChange,
}: DraggableLineSelectionProps) => {
    const { t } = useTranslation('general')
    const idCounter = useRef(rows.length)
    const [dragging, setDragging] = useState(false)
    const linesRef = useRef<HTMLDivElement>(null)
    const scrollToNewRow = useRef(false)
    const { isMobile } = useWindowSize()
    const MAX_LINES_ALLOWED = Math.min(MAX_LINES_CAN_BE_SELECTED, availableLines.length)

    // Keep a ref so handlers always read fresh rows without stale-closure issues.
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const sensors = useSensors(
        // a mouse drags at once, a finger has to hold the icon (a quick swipe scrolls)
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const resolveFilledLines = useCallback(
        (current: RowItem[]): LineOption[] =>
            current
                .map((row) => availableLines.find((l) => l.value === row.value))
                .filter(Boolean) as LineOption[],
        [availableLines]
    )

    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            setDragging(false)
            if (!over || active.id === over.id) return

            const prev = rowsRef.current
            const newRows = arrayMove(
                prev,
                prev.findIndex((r) => r.id === active.id),
                prev.findIndex((r) => r.id === over.id)
            )
            setRows(newRows)
            onChangeRef.current?.(resolveFilledLines(newRows))
        },
        [setRows, resolveFilledLines]
    )

    const handleLineChange = useCallback(
        (index: number, value: string) => {
            const prev = rowsRef.current
            const newRows = [...prev]
            newRows[index] = { ...newRows[index], value }
            setRows(newRows)
            onChangeRef.current?.(resolveFilledLines(newRows))
        },
        [setRows, resolveFilledLines]
    )

    const handleMoveLine = useCallback(
        (index: number, offset: -1 | 1) => {
            const prev = rowsRef.current
            const target = index + offset
            if (target < 0 || target >= prev.length) return
            const newRows = arrayMove(prev, index, target)
            setRows(newRows)
            onChangeRef.current?.(resolveFilledLines(newRows))
        },
        [setRows, resolveFilledLines]
    )

    const handleClearLine = useCallback(
        (index: number) => {
            const prev = rowsRef.current
            const newRows =
                prev.length === 1
                    ? [{ id: prev[0].id, value: '' }]
                    : prev.filter((_, i) => i !== index)
            setRows(newRows)
            onChangeRef.current?.(resolveFilledLines(newRows))
        },
        [setRows, resolveFilledLines]
    )

    const handleAddLine = useCallback(() => {
        if (rowsRef.current.length >= MAX_LINES_ALLOWED) return
        scrollToNewRow.current = !isMobile
        setRows((prev) => [...prev, { id: `row-${idCounter.current++}`, value: '' }])
        // No onChange call — an empty row doesn't change the filled selection.
    }, [MAX_LINES_ALLOWED, setRows, isMobile])

    // A new line can be below the visible part of a scrolling dialog or page: scroll down to it (desktop)
    useEffect(() => {
        if (!scrollToNewRow.current) return
        scrollToNewRow.current = false
        linesRef.current?.lastElementChild?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }, [rows.length])

    const getAvailableOptions = (currentIndex: number) => {
        const otherSelected = new Set(
            rows.filter((row, i) => row.value !== '' && i !== currentIndex).map((r) => r.value)
        )
        return availableLines.filter((line) => !otherSelected.has(line.value))
    }

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragStart={() => setDragging(true)}
                onDragCancel={() => setDragging(false)}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                    <div ref={linesRef} className="draggable-line-selection__lines">
                        {rows.map(({ id, value }, index) => {
                            const label = labelPrefix ? `${labelPrefix} ${index + 1}` : `${index + 1}`
                            const selectedLabel =
                                availableLines.find((line) => line.value === value)?.label ?? ''

                            return (
                                <SortableRow key={id} id={id}>
                                    {(dragHandleProps) => {
                                        const selectedRow = (
                                            <div className="draggable-line-selection__selected-wrapper">
                                                <TextField
                                                    id={`line-field-${id}`}
                                                    label={label}
                                                    value={selectedLabel}
                                                    readOnly
                                                    aria-label={`${label} ${index + 1}: ${selectedLabel}`}
                                                />
                                                {rows.length > 1 && !isMobile && (
                                                    <span className="draggable-line-selection__move">
                                                        <button
                                                            type="button"
                                                            className="draggable-line-selection__move-btn"
                                                            aria-label={`${t('general:settings:lineMoveUp')} ${index + 1}`}
                                                            disabled={index === 0}
                                                            onClick={() => handleMoveLine(index, -1)}
                                                        >
                                                            <i className="a-icon boschicon-bosch-ic-up" aria-hidden="true" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="draggable-line-selection__move-btn"
                                                            aria-label={`${t('general:settings:lineMoveDown')} ${index + 1}`}
                                                            disabled={index === rows.length - 1}
                                                            onClick={() => handleMoveLine(index, 1)}
                                                        >
                                                            <i className="a-icon boschicon-bosch-ic-down" aria-hidden="true" />
                                                        </button>
                                                    </span>
                                                )}
                                                {rows.length > 1 && (
                                                    <span
                                                        className="draggable-line-selection__drag-icon"
                                                        {...dragHandleProps}
                                                        aria-label={`Reorder line ${index + 1}`}
                                                    >
                                                        <i className="a-icon boschicon-bosch-ic-drag-handle" />
                                                    </span>
                                                )}
                                                {!isMobile && (
                                                    <Button
                                                        mode="integrated"
                                                        icon="delete"
                                                        aria-label={`Remove line ${index + 1}`}
                                                        onClick={() => handleClearLine(index)}
                                                        className="draggable-line-selection__remove-btn"
                                                    />
                                                )}
                                            </div>
                                        )

                                        if (value) {
                                            return isMobile ? (
                                                <SwipeToDeleteRow
                                                    deleteLabel={`Remove line ${index + 1}`}
                                                    onDelete={() => handleClearLine(index)}
                                                >
                                                    {selectedRow}
                                                </SwipeToDeleteRow>
                                            ) : (
                                                selectedRow
                                            )
                                        }

                                        return isMobile ? (
                                            <LinePicker
                                                label={label}
                                                options={getAvailableOptions(index)}
                                                disabled={dragging}
                                                placeholder={placeholder ?? t('settings:linePlaceholder')}
                                                onSelect={(selected) => handleLineChange(index, selected)}
                                            />
                                        ) : (
                                            <FrokMultiSelect
                                                label={label}
                                                options={getAvailableOptions(index)}
                                                value={[]}
                                                isDisabled={dragging}
                                                fixedMenu
                                                placeholder={placeholder ?? t('settings:linePlaceholder')}
                                                onChange={(selected) => {
                                                    if (selected.length > 0) {
                                                        handleLineChange(index, selected[selected.length - 1].value)
                                                    }
                                                }}
                                            />
                                        )
                                    }}
                                </SortableRow>
                            )
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            {rows.length < MAX_LINES_ALLOWED && (
                <Button
                    mode="integrated"
                    icon="add"
                    onClick={handleAddLine}
                    className="draggable-line-selection__add-btn"
                />
            )}
        </>
    )
}

export default DraggableLineSelection
